import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { FblModeComponent } from "@m19-board/strategies/fbl-mode/fbl-mode.component";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { AccountSelectionService, FblModeSize, injectStrategyWarningProvider } from "@front/m19-services";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { CampaignType } from "@front/m19-api-client";
import { IIconComponent, ITooltipDirective } from "@front/m19-ui";
import { switchMap } from "rxjs";
import { StrategyEx } from "@front/m19-models";

@Component({
  selector: "app-strategy-grid-info",
  imports: [FblModeComponent, ITooltipDirective, TranslocoDirective, IIconComponent],
  template: `
    <ng-container *transloco="let t">
      <div class="my-auto flex flex-row items-center">
        @if (strategy(); as strat) {
          <app-fbl-mode [strategy]="strat" [size]="FblModeSize.small" class="mr-1" />
          @if (strat.strategyId! > 0 && tooltip()) {
            <div class="flex" [iTooltip]="tooltip()">
              <IIcon
                name="alertTriangle"
                class="align-center text-xl"
                [class.text-destructive]="isErrorWarning()"
                [class.text-yellow-500]="!isErrorWarning()"
              />
            </div>
            @if (isDayPartingEnabled()) {
              <div class="flex">
                <IIcon name="clock" class="activated align-center ml-2 text-xl" [iTooltip]="daypartingTooltip()" />
              </div>
            }
          }

          @if (strat.primeDayBoost && promoBoostActivated()) {
            <IIcon
              name="boost"
              class="text-main text-xl"
              [iTooltip]="t('strategy-card.boost_has_been_activated_on_this_strategy')"
            />
          }
        }
      </div>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyGridInfoComponent {
  private warningProvider = injectStrategyWarningProvider();
  private translocoService = inject(TranslocoService);
  private accountSelectionService = inject(AccountSelectionService);

  protected readonly CampaignType = CampaignType;
  protected readonly FblModeSize = FblModeSize;

  strategy = input.required<StrategyEx>();

  promoBoostActivated = toSignal(this.accountSelectionService.promoBoostActivated$);

  private warning$ = toObservable(this.strategy).pipe(
    switchMap((strat) => this.warningProvider.getStrategyWarning(strat)),
  );
  warning = toSignal(this.warning$);
  isErrorWarning = computed(() => this.warning()?.alertType === "error");

  tooltip = computed(() => {
    const strategy = this.strategy();

    let tooltip = "";
    if (
      strategy.isAdvertisingStrategy() &&
      strategy.disableOtherQueries &&
      this.strategy()?.campaignType !== CampaignType.SD
    ) {
      tooltip += this.translocoService.translate("strategy-info-cell.ai-powered_targeting_is_deactivated");
    }
    if (this.warning()) {
      tooltip += "\n" + this.warning()?.shortDescription;
    }
    return tooltip;
  });

  daypartingTooltip = computed(() => {
    const strategy = this.strategy();
    if (!strategy || !strategy.isSpStrategy()) return;

    return (
      "Strategy paused from " + strategy.daypartingPauseHour + ":00 to " + strategy.daypartingReactivationHour + ":00"
    );
  });

  isDayPartingEnabled = computed(() => {
    const strategy = this.strategy();
    if (!strategy || !strategy.isSpStrategy()) return;

    return strategy.daypartingPauseHour != null && strategy.daypartingReactivationHour != null;
  });
}
